import { Add, ExpandMore, QrCode } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Switch, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button/Button";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { modalDocumentGroup, modalQR, permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import CreateGroupModal from "../../components/documents/create-group-modal/CreateGroupModal";
import QrCodeModal from "../../components/qr-code-modal/QrCodeModal";
import { ComplaintService } from "../../services/ComplaintService";
import { DocumentsGroupDTO } from "../../types/DocumentsGroupDTO";
import { SGGCompanyDTO } from "../../types/SGGCompanyDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import { obtemIconeOrdem } from "../executions/Executions";
import './ComplaintManagement.scss';

const ComplaintManagement: FC = () => {
	const [openCreate, setOpenCreate] = useAtom(modalDocumentGroup);
	const [, setOpenQR] = useAtom(modalQR);
	const [aba, setAba] = useState<string | false>("");
	const [search, setSearch] = useState('');
	const [ordemAsc, setOrdemAsc] = useState(false);
	const [fieldOrder, setFieldOrder] = useState<'NAME' | 'ACTIVE'>('NAME');
	const [loading, setLoading] = useState(true);
	const [groups, setGroups] = useState<DocumentsGroupDTO[]>([]);
	const [companies, setCompanies] = useState<SGGCompanyDTO[]>([]);
	const [filteredGroups, setFilteredGroups] = useState<DocumentsGroupDTO[]>([]);
	const [qrCodeId, setQrCodeId] = useState('');
	const [permissions] = useAtom(permissionsAtom);
	const service = new ComplaintService();
	const isMobile = useMediaQuery('(max-width: 1024px)');
	const navigate = useNavigate();
	const { showAlert } = useAlert();
	const { verifyPermission } = useUtils();

	function mudaOrdem(field: 'NAME' | 'ACTIVE') {
		setAba(false);
		setFieldOrder(field);
		setOrdemAsc(!ordemAsc);
	}

	const handleChange = (group: DocumentsGroupDTO) => {
		if (!group.active) return;
		if (aba === group.cnpj) {
			setAba(false);
			return;
		}
		setCompanies([]);
		setAba(group.cnpj);
		loadCompaniesOnExpand(group.companyIdGroup);
	};

	const loadGroups = async () => {
		const response = await service.getGroups();
		if (response) {
			setGroups(response.data);
			setFilteredGroups(response.data);
		}
		setLoading(false);
	};

	const loadCompaniesOnExpand = async (id: number) => {
		const response = await service.getcompanies(id);
		if (response && response.data) {
			setCompanies(response.data);
		}
	};

	useEffect(() => {
		if (!search) {
			setFilteredGroups(groups);
		}
		setFilteredGroups(groups.filter(item =>
			item.companyNameGroup.toUpperCase().includes(search.toUpperCase())));
	}, [search]);

	useEffect(() => {
		if (openCreate) return;
		loadGroups();
	}, [openCreate]);

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'MANAGEMENT_COMPLAINTS');
		}
	}, []);

	return (
		<Fragment>
			<AppBar
				modulo="Gestão de denúncias"
			/>
			<div className="complaint-management">
				<div className="top">
					<div className="document-inputs">
						<Box>
							<Button
								fullWidth
								variant="contained"
								color="success"
								onClick={() => navigate('/gestao-de-denuncias/painel')}
							>
								Acessar painel de denúncias
							</Button>
						</Box>
						<Button
							variant="contained"
							color="info"
							startIcon={<Add />}
							onClick={() => setOpenCreate(true)}
						>
							Adicionar empresa
						</Button>
						<TextField
							label="Buscar empresa pelo nome"
							id="group-search"
							size="small"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>
				{!loading && filteredGroups && filteredGroups.length
					? <div className="tabela">
						<div className="header-tabela">
							<p onClick={() => mudaOrdem('NAME')} id="sort">Empresa {obtemIconeOrdem('NAME', fieldOrder, ordemAsc)}</p>
							<p onClick={() => mudaOrdem('ACTIVE')} id="sort-x">Canal ativo {obtemIconeOrdem('ACTIVE', fieldOrder, ordemAsc)}</p>
						</div>
						<>
							{filteredGroups.sort((a, b) => {
								let valueA: string | number;
								let valueB: string | number;
								switch (fieldOrder) {
									case 'ACTIVE':
										valueA = a.active ? 1 : 0;
										valueB = b.active ? 1 : 0;
										break;
									case 'NAME':
										valueA = a.companyNameGroup;
										valueB = b.companyNameGroup;
										break;
								}
								if (typeof valueA === 'string' && typeof valueB === 'string') {
									return ordemAsc
										? valueA.localeCompare(valueB)
										: valueB.localeCompare(valueA);
								} else {
									return ordemAsc
										? (valueA as number) - (valueB as number)
										: (valueB as number) - (valueA as number);
								}
							}).map((group) => (
								<Accordion
									key={group.cnpj}
									expanded={aba === group.cnpj}
									onChange={() => handleChange(group)}
									sx={{backgroundColor: "#282828"}}
									slotProps={{ transition: { unmountOnExit: true } }}
								>
									<AccordionSummary
										expandIcon={<ExpandMore/>}
										aria-controls={group.cnpj}
										id={group.cnpj}
									>
										<Typography variant='h6'>{group.companyNameGroup}</Typography>
										<Tooltip placement="left" title={group.active ? "Desativar canal de denúncias" : "Ativar canal de denúncias"}>
											<Switch
												color="success"
												checked={group.active}
												onClick={(event) => {
													event.stopPropagation();
													update(group.companyIdGroup, !group.active);
													setGroups(prevGroups => prevGroups.map(g => g.companyIdGroup === group.companyIdGroup ? { ...g, active: !g.active } : g));
													setFilteredGroups(prevGroups => prevGroups.map(g => g.companyIdGroup === group.companyIdGroup ? { ...g, active: !g.active } : g));
												}}
											/>
										</Tooltip>
									</AccordionSummary>
									<AccordionDetails>
										{companies.length
											? companies.map((company) => (
												<div key={company.id_empresa} className="company">
													<Typography flex={1} variant="h6">{company.nome}</Typography>
													{isMobile
														? <></>
														: <Typography flex={1} variant='h6'>{company.CNPJ}</Typography>
													}
													<Tooltip placement="left" title="QR Code para este canal de denúncias">
														<IconButton onClick={() => {
															setQrCodeId(company.id_empresa);
															setOpenQR(true);
														}}>
															<QrCode/>
														</IconButton>
													</Tooltip>
												</div>
											))
											: getLoading()
										}
									</AccordionDetails>
								</Accordion>
							))}
						</>
					</div>
				: !loading
					? <div className="sem-execucoes">
						<Typography variant="h5">
							Nenhum canal ativo ainda...
						</Typography>
					</div>
					: <></>
				}
			</div>
			{loading
				? getLoading()
				: <></>
			}
			<CreateGroupModal
				create={(cnpj) => service.createGroup(cnpj)}
				cnpjs={groups.map(group => group.cnpj)}
			/>
			<QrCodeModal
				module="o canal de denúncias"
				link={`https://interno.inovação.seg.br/canal-de-denuncias/${qrCodeId}`}
			/>
		</Fragment>
	);

	async function update(id: number, active: boolean) {
		await service.update(id, active);
		showAlert('Canal de denúncias atualizado com sucesso', "success");
	}
};

export default ComplaintManagement;